<script setup>
// SiteUp: tela de início de uma área do Hub (hub/:worldId), na versão
// "ação primeiro" escolhida em 12/09/2026. Um destaque com o que a área faz
// e os números vivos; o resto em blocos menores. O que cada área mostra está
// em HUB_HOMES (hubWorlds.js); os números vêm de useHubWorldStats.
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { emitter } from 'shared/helpers/mitt';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';
import { HUB_HOMES } from './hubWorlds';
import AttendanceHome from './AttendanceHome.vue';
import SalesHome from './SalesHome.vue';
import './hubDesign.css';
import { useHubWorlds } from './useHubWorlds';
import { useHubWorldStats } from './useHubWorldStats';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { allWorlds } = useHubWorlds();
const { isFeatureFlagEnabled } = usePolicy();

const worldId = computed(() => String(route.params.worldId || ''));
const world = computed(
  () => allWorlds.value.find(item => item.id === worldId.value) || null
);
const home = computed(() => HUB_HOMES[worldId.value] || null);
// Atender e Vender têm tela própria (AttendanceHome, SalesHome), que carrega
// os próprios dados. Passar a área para o useHubWorldStats nesses dois casos
// repetia inbox, CSAT, tarefas, relatório e agenda a cada abertura.
const OWN_HOME_WORLDS = ['attendance', 'sales'];
const statsWorldId = computed(() =>
  OWN_HOME_WORLDS.includes(worldId.value) ? '' : worldId.value
);
const {
  hero,
  rows,
  listLoaded,
  listForbidden,
  listSummary,
  hints,
  badges,
  chipCounts,
  inboxChips,
} = useHubWorldStats(statsWorldId);

const hubRoute = computed(() => accountScopedRoute('focus_hub'));
const goToHub = () => router.push(hubRoute.value);

// Área desconhecida (ou sem destino liberado para a pessoa): volta ao Hub.
// Área sem tela de início (Medir, Configurar) também: antes abria em branco.
// A Central da IA tem tela própria, e o Hub já leva direto para ela.
watch(
  [world, home],
  ([current, currentHome]) => {
    if (current?.id === 'ai_center') {
      router.replace(
        current.primaryDestination?.to || accountScopedRoute('ai_center')
      );
      return;
    }
    if (!current || !currentHome) router.replace(hubRoute.value);
  },
  { immediate: true }
);

// Resolve um item da tela (HUB_HOMES) contra os destinos que a pessoa pode
// ver. Sem destino, o item some. `query` do item entra na rota do destino.
const resolve = item => {
  const destination = world.value?.destinations.find(
    candidate => candidate.id === item.destinationId
  );
  if (!destination) return null;
  const to = item.query
    ? { ...destination.to, query: { ...destination.to.query, ...item.query } }
    : destination.to;
  return {
    ...destination,
    ...item,
    id: item.id || destination.id,
    destinationId: destination.id,
    to,
    label: t(item.labelKey || destination.labelKey),
    subtitle: t(item.subtitleKey || destination.subtitleKey),
  };
};
const resolveAll = items => (items || []).map(resolve).filter(Boolean);

const actions = computed(() => resolveAll(home.value?.actions));
const chips = computed(() => {
  const config = home.value?.chips;
  if (!config) return [];
  const presets = resolveAll(config.items).map(chip => ({
    ...chip,
    count: chip.count ? chipCounts.value[chip.count] : null,
  }));
  const perInbox = config.inboxes
    ? inboxChips.value.map(chip => ({
        ...chip,
        to: accountScopedRoute(chip.routeName, chip.params),
      }))
    : [];
  return [...presets, ...perInbox];
});
const channels = computed(() => resolveAll(home.value?.channels));
const columns = computed(() =>
  (home.value?.columns || [])
    .map(column => ({ ...column, items: resolveAll(column.items) }))
    .filter(column => column.items.length)
);
const rail = computed(() => resolveAll(home.value?.rail?.items));
const strip = computed(() => resolveAll(home.value?.strip?.items));
const links = computed(() => resolveAll(home.value?.links));

// Ações de Marketing: o canal escolhido no destaque é para onde "Criar" leva.
const selectedChannelId = ref('');
const selectedChannel = computed(
  () =>
    channels.value.find(channel => channel.id === selectedChannelId.value) ||
    channels.value[0] ||
    null
);

// Linhas da lista viva apontam para um destino da área (campanhas) ou para
// uma rota própria (editor do fluxo).
const rowTo = row => {
  if (row.routeName) return accountScopedRoute(row.routeName, row.params);
  const destination = world.value?.destinations.find(
    candidate => candidate.id === row.destinationId
  );
  return destination?.to || null;
};

// A Central de Agendamento abre por cima da tela quando a conta tem a flag;
// sem ela, o bloco Agenda abre a visão de agenda do Kanban.
const showAgendaCard = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.AGENDA_CENTRAL)
);

const openDestination = destination => {
  if (destination.destinationId === 'agenda') {
    if (showAgendaCard.value) {
      emitter.emit('agenda:quick:open');
      return;
    }
  }
  router.push(destination.to);
};

const openRow = row => {
  const to = rowTo(row);
  if (to) router.push(to);
};
</script>

<template>
  <div
    class="hub-area flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto bg-n-background"
    :class="
      world && OWN_HOME_WORLDS.includes(world.id)
        ? 'hub-v3'
        : 'px-5 py-[clamp(30px,5vw,60px)] sm:px-6'
    "
    data-testid="hub-world-home"
  >
    <!-- Atender já está na versão aprovada em 12/09 (fila com celular); as
         outras áreas seguem na composição de HUB_HOMES até a próxima rodada. -->
    <AttendanceHome v-if="world && world.id === 'attendance'" :world="world" />
    <SalesHome v-else-if="world && world.id === 'sales'" :world="world" />
    <section v-else-if="world && home" class="w-full max-w-[1180px]">
      <button type="button" class="hub-back min-h-11" @click="goToHub">
        <span class="i-ri-arrow-left-line size-4" aria-hidden="true" />
        {{ t('HUB.AREA_BACK') }}
      </button>

      <header class="mb-4 mt-5 flex min-w-0 items-center gap-3">
        <span class="hub-ico">
          <BrasaMascot
            v-if="world.id === 'automation'"
            state="idle"
            :size="20"
            :animated="false"
          />
          <span v-else class="size-5" :class="[world.icon]" />
        </span>
        <div class="min-w-0">
          <h1
            class="m-0 text-[22px] font-medium tracking-tight text-n-slate-12"
          >
            {{ t(world.labelKey) }}
          </h1>
          <p class="m-0 text-[13px] text-n-slate-11">
            {{ t(world.descriptionKey) }}
          </p>
        </div>
      </header>

      <section
        class="hub-hero"
        :class="`hub-hero--${world.id}`"
        data-testid="hub-hero"
      >
        <div class="min-w-0">
          <h2 class="hub-hero-title">{{ hero.title }}</h2>
          <p v-if="hero.subtitle" class="hub-hero-sub">{{ hero.subtitle }}</p>
          <div v-if="hero.kpis.length" class="hub-hero-kpis">
            <div
              v-for="item in hero.kpis"
              :key="item.id"
              :data-testid="`hub-kpi-${item.id}`"
            >
              <b>{{ item.value }}</b>
              <span>{{ item.label }}</span>
            </div>
          </div>
          <div
            v-if="channels.length"
            class="hub-chips mt-3.5"
            role="radiogroup"
            :aria-label="t('HUB.HOME.CHANNEL_LABEL')"
          >
            <button
              v-for="channel in channels"
              :key="channel.id"
              type="button"
              role="radio"
              class="hub-chip hub-chip--hero"
              :aria-checked="selectedChannel?.id === channel.id"
              :data-testid="`hub-channel-${channel.id}`"
              @click="selectedChannelId = channel.id"
            >
              <span class="size-4" :class="[channel.icon]" aria-hidden="true" />
              {{ channel.label }}
            </button>
          </div>
        </div>
        <div class="hub-hero-actions">
          <button
            v-for="(action, index) in actions"
            :key="action.id"
            type="button"
            class="hub-pill min-h-11"
            :class="action.ghost ? 'hub-pill--ghost' : 'hub-pill--light'"
            :data-testid="
              index === 0
                ? `hub-primary-${world.id}`
                : `hub-action-${action.id}`
            "
            @click="openDestination(action)"
          >
            {{ action.label }}
          </button>
          <button
            v-if="selectedChannel"
            type="button"
            class="hub-pill hub-pill--light min-h-11"
            :data-testid="`hub-primary-${world.id}`"
            @click="openDestination(selectedChannel)"
          >
            {{ t('HUB.HOME.CREATE') }}
          </button>
        </div>
      </section>

      <section v-if="chips.length" class="mt-5">
        <h2 class="hub-kicker">{{ t(home.chips.labelKey) }}</h2>
        <div class="hub-chips">
          <button
            v-for="chip in chips"
            :key="chip.id"
            type="button"
            class="hub-chip min-h-9"
            :data-testid="`hub-chip-${chip.id}`"
            @click="openDestination(chip)"
          >
            <span
              v-if="chip.icon"
              class="size-4"
              :class="[chip.icon]"
              aria-hidden="true"
            />
            {{ chip.label }}
            <span v-if="chip.count" class="hub-chip-count">{{
              chip.count
            }}</span>
          </button>
        </div>
      </section>

      <div v-if="columns.length" class="mt-6 grid gap-5 lg:grid-cols-2">
        <section v-for="column in columns" :key="column.id">
          <h2 class="hub-kicker">{{ t(column.labelKey) }}</h2>
          <div class="grid gap-3">
            <button
              v-for="destination in column.items"
              :key="destination.id"
              type="button"
              class="hub-card hub-card--dest min-h-11"
              :class="{ 'hub-card--agenda': destination.id === 'agenda' }"
              :data-testid="`hub-dest-${destination.id}`"
              @click="openDestination(destination)"
            >
              <span class="hub-ico">
                <span class="size-5" :class="[destination.icon]" />
              </span>
              <span class="hub-dest-copy">
                <span class="hub-name">{{ destination.label }}</span>
                <span class="hub-meta">{{
                  hints[destination.id] || destination.subtitle
                }}</span>
              </span>
              <span class="hub-dest-end hub-route">
                <span
                  v-if="badges[destination.id]"
                  class="hub-dest-badge"
                  :class="`hub-dest-badge--${badges[destination.id].tone}`"
                >
                  {{ badges[destination.id].value }}
                </span>
                <span class="i-ri-arrow-right-line size-4" />
              </span>
            </button>
          </div>
        </section>
      </div>

      <div
        v-if="home.list"
        class="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]"
      >
        <section>
          <div class="mb-2 flex items-baseline justify-between gap-3">
            <h2 class="hub-kicker mb-0">{{ t(home.list.labelKey) }}</h2>
            <span v-if="listSummary" class="text-xs text-n-slate-11">{{
              listSummary
            }}</span>
          </div>
          <div class="hub-list" :data-testid="`hub-list-${home.list.id}`">
            <button
              v-for="row in rows"
              :key="row.id"
              type="button"
              class="hub-row min-h-11"
              :data-testid="`hub-row-${row.id}`"
              @click="openRow(row)"
            >
              <span class="hub-row-copy">
                <span class="hub-row-title">{{ row.title }}</span>
                <span v-if="row.meta" class="hub-row-meta">{{ row.meta }}</span>
              </span>
              <span class="hub-badge" :class="`hub-badge--${row.badge.tone}`">
                {{ row.badge.label }}
              </span>
              <span v-if="row.time" class="hub-row-time">{{ row.time }}</span>
            </button>
            <p v-if="listLoaded && !rows.length" class="hub-empty">
              {{
                listForbidden
                  ? t('HUB.HOME.NOT_FOR_ROLE')
                  : t(home.list.emptyKey)
              }}
            </p>
          </div>
        </section>
        <section v-if="rail.length">
          <h2 class="hub-kicker">{{ t(home.rail.labelKey) }}</h2>
          <div class="grid gap-3">
            <button
              v-for="destination in rail"
              :key="destination.id"
              type="button"
              class="hub-card hub-card--dest hub-card--rail min-h-11"
              :data-testid="`hub-dest-${destination.id}`"
              @click="openDestination(destination)"
            >
              <span class="hub-ico">
                <BrasaMascot
                  v-if="destination.destinationId === 'captain_faqs'"
                  state="idle"
                  :size="20"
                  :animated="false"
                />
                <span v-else class="size-5" :class="[destination.icon]" />
              </span>
              <span class="hub-dest-copy">
                <span class="hub-name">{{ destination.label }}</span>
                <span class="hub-meta">{{
                  hints[destination.id] || destination.subtitle
                }}</span>
              </span>
              <span class="hub-dest-end hub-route">
                <span class="i-ri-arrow-right-line size-4" />
              </span>
            </button>
          </div>
        </section>
      </div>

      <section v-if="strip.length" class="mt-6">
        <h2 class="hub-kicker">{{ t(home.strip.labelKey) }}</h2>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <button
            v-for="destination in strip"
            :key="destination.id"
            type="button"
            class="hub-card hub-card--dest hub-card--strip min-h-11"
            :data-testid="`hub-dest-${destination.id}`"
            @click="openDestination(destination)"
          >
            <span class="hub-ico">
              <span class="size-5" :class="[destination.icon]" />
            </span>
            <span class="hub-dest-copy">
              <span class="hub-name">{{ destination.label }}</span>
            </span>
            <span class="hub-dest-end hub-route">
              <span class="i-ri-arrow-right-line size-4" />
            </span>
          </button>
        </div>
      </section>

      <nav
        v-if="links.length"
        class="mt-5 flex flex-wrap gap-x-5 gap-y-2"
        :aria-label="t('HUB.HOME.LINKS_LABEL')"
      >
        <button
          v-for="destination in links"
          :key="destination.id"
          type="button"
          class="hub-link min-h-9"
          :data-testid="`hub-link-${destination.id}`"
          @click="openDestination(destination)"
        >
          <span class="size-4" :class="[destination.icon]" aria-hidden="true" />
          {{ destination.label }}
        </button>
      </nav>
    </section>
  </div>
</template>

<style scoped>
.hub-card {
  position: relative;
  display: flex;
  overflow: hidden;
  text-align: start;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--border-weak));
  @apply rounded-[var(--workspace-radius,16px)];
  cursor: pointer;
  font-family: inherit;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    border-color var(--motion-duration-hover) var(--motion-ease-out),
    transform var(--motion-duration-press) var(--motion-ease-out);
}
.hub-card:hover {
  background: rgb(var(--solid-2));
}
.hub-card:active {
  transform: scale(0.99);
}
.hub-card:hover .hub-route :last-child {
  transform: translateX(3px);
}
.hub-card--dest {
  flex-direction: row;
  align-items: center;
  min-height: 76px;
  justify-content: flex-start;
  gap: 13px;
  padding: 14px 15px;
}
.hub-card--rail {
  min-height: 64px;
  padding: 11px 15px;
}
.hub-card--strip {
  min-height: 60px;
  padding: 10px 14px;
}
.hub-card--dest .hub-name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hub-card--dest .hub-meta {
  margin-top: 3px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Agenda em teal, como era o bloco do Hub: acesso rápido, sem competir
   com o roxo do Atendimento. Cor só por token. */
.hub-card--agenda {
  background: linear-gradient(
    160deg,
    rgb(var(--teal-9) / 0.14),
    rgb(var(--teal-9) / 0.03)
  );
  border-color: rgb(var(--teal-9) / 0.5);
}
.hub-card--agenda:hover {
  background: linear-gradient(
    160deg,
    rgb(var(--teal-9) / 0.2),
    rgb(var(--teal-9) / 0.05)
  );
}
.hub-card--agenda .hub-ico {
  background: rgb(var(--teal-9) / 0.18);
  color: rgb(var(--teal-11));
}
/* Texto no --teal-12 (10.4:1 no claro); o --teal-11 dá só 3.93:1, abaixo
   do 4.5 que 12px e 13px exigem. O ícone segue no --teal-11 (3:1 basta). */
.hub-card--agenda .hub-count {
  border-color: rgb(var(--teal-9) / 0.4);
  color: rgb(var(--teal-12));
}
.hub-card--agenda .hub-route {
  color: rgb(var(--teal-12));
}
.hub-dest-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.hub-dest-end {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgb(var(--slate-11));
}
.hub-route :last-child {
  transition: transform 220ms var(--motion-ease-out);
}
.hub-dest-badge {
  display: inline-flex;
  min-width: 28px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 9999px;
  background: rgb(var(--blue-9));
  color: rgb(var(--slate-1));
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.hub-dest-badge--amber {
  background: rgb(var(--amber-9) / 0.18);
  color: rgb(var(--amber-12));
}
.hub-ico {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  border-radius: 9999px;
  background: rgb(var(--solid-3));
  color: rgb(var(--slate-12));
}
.hub-name {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: rgb(var(--slate-12));
}
.hub-meta {
  max-width: 48ch;
  margin: 7px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgb(var(--slate-11));
}
.hub-kicker {
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--slate-10));
}
/* Destaque: o mesmo mundo visual do card de Atendimento no Hub (gradiente
   escuro em hex, texto claro), com uma cor por área. Não muda com o tema,
   como o card não muda. */
.hub-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  padding: 22px 26px;
  color: #f4f2ff;
  @apply rounded-[var(--workspace-radius,20px)];
  background: radial-gradient(60% 80% at 20% 20%, #8b6bff 0%, transparent 60%),
    radial-gradient(50% 70% at 85% 75%, #a78bfa 0%, transparent 55%),
    linear-gradient(135deg, #1a0f3d 0%, #2e1b6b 46%, #4b2fa8 100%);
}
.hub-hero--sales {
  background: radial-gradient(60% 80% at 20% 20%, #38bdf8 0%, transparent 60%),
    linear-gradient(135deg, #0b2a4a 0%, #0f4c81 60%, #1d9bf0 100%);
}
.hub-hero--marketing {
  background: radial-gradient(60% 80% at 20% 20%, #fb923c 0%, transparent 60%),
    linear-gradient(135deg, #4a1d0b 0%, #9a3412 55%, #f97316 100%);
}
.hub-hero--automation {
  background: radial-gradient(60% 80% at 20% 20%, #2dd4bf 0%, transparent 60%),
    linear-gradient(135deg, #062a2a 0%, #0f5f5c 55%, #14b8a6 100%);
}
@media (max-width: 760px) {
  .hub-hero {
    grid-template-columns: 1fr;
  }
}
.hub-hero-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: inherit;
  text-wrap: balance;
}
.hub-hero-sub {
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.45;
  opacity: 0.88;
}
.hub-hero-kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 22px;
  margin-top: 14px;
}
.hub-hero-kpis b {
  display: block;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.hub-hero-kpis span {
  font-size: 12px;
  opacity: 0.82;
}
.hub-hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}
.hub-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: 9999px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    transform var(--motion-duration-press) var(--motion-ease-out);
}
.hub-pill:active {
  transform: scale(0.96);
}
.hub-pill--light {
  background: #f4f2ff;
  color: #241553;
}
.hub-pill--light:hover {
  background: #ffffff;
}
.hub-pill--ghost {
  background: transparent;
  color: #f4f2ff;
  border-color: rgb(244 242 255 / 0.5);
}
.hub-pill--ghost:hover {
  background: rgb(244 242 255 / 0.12);
}
.hub-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.hub-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid rgb(var(--border-strong));
  border-radius: 9999px;
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-11));
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    color var(--motion-duration-hover) var(--motion-ease-out);
}
.hub-chip:hover {
  background: rgb(var(--solid-2));
  color: rgb(var(--slate-12));
}
.hub-chip-count {
  padding: 0 6px;
  border-radius: 9999px;
  background: rgb(var(--solid-3));
  color: rgb(var(--slate-12));
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
/* Chips dentro do destaque: claros sobre o gradiente; o escolhido inverte. */
.hub-chip--hero {
  border-color: rgb(244 242 255 / 0.45);
  background: transparent;
  color: #f4f2ff;
}
.hub-chip--hero:hover {
  background: rgb(244 242 255 / 0.12);
  color: #ffffff;
}
.hub-chip--hero[aria-checked='true'] {
  background: #f4f2ff;
  border-color: #f4f2ff;
  color: #241553;
}
.hub-list {
  overflow: hidden;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--border-weak));
  @apply rounded-[var(--workspace-radius,16px)];
}
.hub-row {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  border: 0;
  border-top: 1px solid rgb(var(--border-weak));
  background: transparent;
  text-align: start;
  font-family: inherit;
  cursor: pointer;
  transition: background var(--motion-duration-hover) var(--motion-ease-out);
}
.hub-row:first-child {
  border-top: 0;
}
.hub-row:hover {
  background: rgb(var(--solid-2));
}
.hub-row-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.hub-row-title {
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--slate-12));
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hub-row-meta {
  overflow: hidden;
  font-size: 12px;
  color: rgb(var(--slate-11));
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hub-row-time {
  font-size: 12px;
  color: rgb(var(--slate-10));
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.hub-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  background: rgb(var(--solid-3));
  color: rgb(var(--slate-11));
}
.hub-badge--green {
  background: rgb(var(--teal-9) / 0.16);
  color: rgb(var(--teal-12));
}
.hub-badge--amber {
  background: rgb(var(--amber-9) / 0.18);
  color: rgb(var(--amber-12));
}
.hub-badge--blue {
  background: rgb(var(--blue-9) / 0.16);
  color: rgb(var(--blue-12));
}
.hub-empty {
  margin: 0;
  padding: 18px 16px;
  font-size: 13px;
  color: rgb(var(--slate-11));
}
.hub-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: rgb(var(--slate-11));
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.hub-link:hover {
  color: rgb(var(--slate-12));
  text-decoration: underline;
}
.hub-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.75rem;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: rgb(var(--slate-11));
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    color var(--motion-duration-hover) var(--motion-ease-out);
}
.hub-back:hover {
  background: rgb(var(--solid-2));
  color: rgb(var(--slate-12));
}
</style>
